/**
 * @author        matt@zumper.com (Matthieu Devlin)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   Notification Settings Reducer
 */

import type { NotificationSettings } from '@zcode/state-type'
import { filterActions } from '@zumper/redux-ignore'
import type { ExtractActions, Values } from 'redux-actions'
import {
  GET_NOTIFICATION_SETTINGS,
  RESET_NOTIFICATION_START_DATE,
  SAVE_NOTIFICATION_SETTINGS,
  SAVE_NOTIFICATION_SETTINGS_ERROR,
  SAVE_NOTIFICATION_SETTINGS_SUCCESS,
  SNOOZE_NOTIFICATIONS,
  TOGGLE_NOTIFICATIONS,
  UPDATE_NOTIFICATION_SETTING,
} from '../../../../shared/actions/constants'
import {
  DEFAULT_PUSH_SEARCH_FREQUENCY,
  EMAIL,
  PUSH,
  SEARCHES,
  SEARCH_FREQUENCY,
} from '../../../../shared/components/notification_settings/constants'
import { omapFiltered } from '../../../../shared/utils/utils'
import type * as actions from '../../../actions'

const getBlueshiftNotificationSettingName = <
  Type extends 'email' | 'push',
  Setting extends keyof NotificationSettings[Type] & string,
>(
  type: Type,
  setting: Setting
) => `unsub_${type}_${setting}`

const getUnsubscribedSettings = (
  emailSettings: NotificationSettings['email'],
  pushSettings: NotificationSettings['push']
) => {
  const emailUnsubs = omapFiltered(emailSettings, (...[v, k]) => {
    return v ? undefined : getBlueshiftNotificationSettingName(EMAIL, k)
  })
  const pushUnsubs = omapFiltered(pushSettings, (...[v, k]) => {
    return v ? undefined : getBlueshiftNotificationSettingName(PUSH, k)
  })
  return Array.from(new Set(emailUnsubs.concat(pushUnsubs)))
}

const whitelist = [
  GET_NOTIFICATION_SETTINGS,
  RESET_NOTIFICATION_START_DATE,
  SAVE_NOTIFICATION_SETTINGS,
  SAVE_NOTIFICATION_SETTINGS_ERROR,
  SAVE_NOTIFICATION_SETTINGS_SUCCESS,
  SNOOZE_NOTIFICATIONS,
  TOGGLE_NOTIFICATIONS,
  UPDATE_NOTIFICATION_SETTING,
] as const

type Actions = ExtractActions<Values<typeof actions>>

const notificationSettingsReducer = function notificationSettings(
  state: NotificationSettings = {
    blueshiftInitialUnsubs: [],
    blueshiftSessionUnsubs: [],
    email: {}, // ImMap
    enabled: true,
    hasPush: false,
    isSaving: false,
    lastSaveSucceeded: true,
    push: {}, // ImMap
    snoozeFor: 10,
    startDate: null,
    user: {}, // ImMap
  },
  action: Actions
): NotificationSettings {
  switch (action.type) {
    case GET_NOTIFICATION_SETTINGS: {
      const {
        email,
        enabled,
        has_push: hasPush,
        push,
        start_date: startDate,
        user,
      } = action.payload.settings
      return {
        ...state,
        blueshiftInitialUnsubs: getUnsubscribedSettings(email, push),
        blueshiftSessionUnsubs: [],
        email,
        enabled,
        hasPush,
        // Add default push frequency to push settings if neccessary
        push: push[SEARCH_FREQUENCY]
          ? push
          : {
              ...push,
              [SEARCH_FREQUENCY]: DEFAULT_PUSH_SEARCH_FREQUENCY,
            },
        startDate,
        user,
      }
    }
    case RESET_NOTIFICATION_START_DATE: {
      return { ...state, startDate: null }
    }
    case SAVE_NOTIFICATION_SETTINGS: {
      return { ...state, isSaving: true }
    }
    case SAVE_NOTIFICATION_SETTINGS_ERROR: {
      return { ...state, lastSaveSucceeded: false, isSaving: false }
    }
    case SAVE_NOTIFICATION_SETTINGS_SUCCESS: {
      const emailSettings = state.email
      const pushSettings = state.push

      return {
        ...state,
        blueshiftInitialUnsubs: getUnsubscribedSettings(emailSettings, pushSettings),
        blueshiftSessionUnsubs: [],
        lastSaveSucceeded: true,
        isSaving: false,
      }
    }
    case SNOOZE_NOTIFICATIONS: {
      const value = action.payload.value
      return { ...state, snoozeFor: value }
    }
    case TOGGLE_NOTIFICATIONS: {
      const enabled = action.payload.enabled
      return { ...state, enabled, startDate: null }
    }
    case UPDATE_NOTIFICATION_SETTING: {
      const { type, setting, value } = action.payload.data
      const current = state[type] || {}
      const updatedState = { ...state, [type]: { ...current, [setting]: value } }

      // keep a record of blueshift notifications user is unsubscribing from - searches are not
      // sent from blueshift so can ignore
      if (setting !== SEARCHES) {
        const sessionUnsubs = state.blueshiftSessionUnsubs
        const initialUnsubs = state.blueshiftInitialUnsubs
        const settingName = getBlueshiftNotificationSettingName(type, setting)
        // we store subscriptions in redux but blueshift stores unsubscriptions so need to remove
        // from unsubs if value is truthy (subscribed) and add if falsey (unsubcribed)
        if (value) {
          updatedState.blueshiftSessionUnsubs = sessionUnsubs.filter(
            (other) => other !== settingName
          )
        } else if (!initialUnsubs.includes(settingName)) {
          // only add to session unsubs if the user wasn't initially unsubbed
          updatedState.blueshiftSessionUnsubs = sessionUnsubs.concat(settingName)
        }
      }

      return updatedState
    }
    default: {
      return state
    }
  }
}

export default filterActions(notificationSettingsReducer, whitelist)
